import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, XCircle, CheckCircle2, TrendingUp, LayoutList,
  MessagesSquare, Sparkles, Wrench, ClipboardCheck, Building2, Quote,
} from "lucide-react";
import { MaskedLines, FadeUp, CountUp, Marquee, EASE } from "@/components/motion";
import { Container, SectionHeading } from "@/components/Section";
import { AccordionItem } from "@/components/Accordion";
import LeadForm from "@/components/LeadForm";
import PropertyCard from "@/components/PropertyCard";
import useProperties from "@/hooks/useProperties";
import { IMAGES, SERVICES, PAINS, DIFFERENCE, STEPS, WHO, FAQS, MARQUEE_ITEMS } from "@/data/content";

const ICONS = { TrendingUp, LayoutList, MessagesSquare, Sparkles, Wrench, ClipboardCheck };

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  return (
    <section ref={ref} className="noise-dark relative overflow-hidden bg-forest text-cream">
      <div className="pointer-events-none absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-forest-700/30 blur-[140px]" />
      <Container className="relative z-10 grid items-center gap-16 pb-24 pt-40 lg:grid-cols-12 lg:pb-32 lg:pt-48">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="inline-flex items-center gap-2.5 rounded-full border border-sage/30 bg-sage/10 px-4 py-2"
            data-testid="hero-badge"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-sage" />
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-sage-light">
              Dublin Short-Term & Corporate Rental Management
            </span>
          </motion.div>

          <MaskedLines
            className="mt-8"
            delay={0.15}
            data-testid="hero-masked-headline"
            lineClassName="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.06] tracking-tight"
            lines={[
              <span key="1" data-testid="hero-masked-headline">Turn your Dublin property</span>,
              <span key="2">into a better-performing</span>,
              <span key="3" className="italic text-sage-light">short-term rental.</span>,
            ]}
          />

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.65 }}
            className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-cream/70"
          >
            FieldGreen manages the entire operation — from pricing and bookings to guests, cleaning and
            maintenance — giving Dublin landlords a genuinely hands-off property management solution.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.8 }}
            className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            <Link
              to="/assessment"
              data-testid="hero-lead-cta"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-cream px-7 py-4 text-sm font-semibold text-forest transition-colors duration-300 hover:bg-sage hover:text-forest-900"
            >
              See What Your Property Could Earn
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/how-it-works"
              data-testid="hero-secondary-cta"
              className="group inline-flex items-center justify-center gap-2 rounded-full border border-cream/25 px-7 py-4 text-sm font-semibold text-cream transition-colors duration-300 hover:border-sage hover:text-sage-light"
            >
              How FieldGreen Works
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs font-medium uppercase tracking-[0.18em] text-cream/45"
            data-testid="hero-trust-line"
          >
            <span>End-to-end management</span>
            <span className="h-1 w-1 rounded-full bg-sage/60" />
            <span>Local Dublin expertise</span>
            <span className="h-1 w-1 rounded-full bg-sage/60" />
            <span>One point of contact</span>
          </motion.p>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, ease: EASE, delay: 0.4 }}
            className="relative"
          >
            <div className="overflow-hidden rounded-2xl border border-white/10 shadow-[0_40px_100px_rgba(0,0,0,0.45)]">
              <motion.img
                src={IMAGES.hero}
                alt="A premium Dublin apartment interior managed by FieldGreen"
                style={{ y: imgY, scale: 1.15 }}
                className="h-[420px] w-full object-cover sm:h-[520px]"
                data-testid="hero-image"
              />
            </div>
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
              className="absolute -bottom-8 -left-4 sm:-left-10 rounded-xl border border-white/10 bg-forest-800/90 p-5 backdrop-blur-xl shadow-[0_24px_60px_rgba(0,0,0,0.4)]"
              data-testid="hero-stat-card"
            >
              <p className="font-mono text-2xl font-semibold text-sage-light">€12,000+</p>
              <p className="mt-1 max-w-[200px] text-[11px] leading-snug text-cream/60">
                Revenue generated across managed properties in a recent month
              </p>
            </motion.div>
          </motion.div>
        </div>
      </Container>
    </section>
  );
}

function Problem() {
  return (
    <section className="bg-bone py-28 sm:py-36">
      <Container className="grid gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <SectionHeading
            index="01"
            eyebrow="The landlord problem"
            title="Airbnb can be lucrative. Managing it shouldn't be a full-time job."
            copy="Short-term rentals in Dublin can outperform long-term tenancies — but the work behind a well-run property is relentless, and it never clocks off."
          />
        </div>
        <div className="lg:col-span-7">
          <div className="grid gap-x-10 gap-y-5 sm:grid-cols-2">
            {PAINS.map((p, i) => (
              <FadeUp key={p} delay={i * 0.05}>
                <div className="flex items-start gap-3" data-testid={`pain-item-${i}`}>
                  <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-forest-700/50" />
                  <p className="text-sm leading-relaxed text-charcoal/75">{p}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.35}>
            <div className="mt-12 rounded-2xl border border-forest/15 bg-cream p-8" data-testid="problem-solution-card">
              <div className="flex items-start gap-4">
                <CheckCircle2 className="mt-1 h-6 w-6 shrink-0 text-forest-700" />
                <div>
                  <p className="font-serif text-2xl font-medium text-forest">FieldGreen takes the entire thing off your hands.</p>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/70">
                    One professional operator for pricing, guests, cleaning, maintenance and everything in between.
                    You get a monthly statement — not a second job.
                  </p>
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </Container>
    </section>
  );
}

function ServicesGrid() {
  return (
    <section className="bg-cream py-28 sm:py-36">
      <Container>
        <SectionHeading
          index="02"
          eyebrow="What FieldGreen manages"
          title="Every part of the operation. None of it on your desk."
          copy="Six pillars, one accountable team. This is what hands-off actually looks like."
        />
        <div className="mt-16 grid gap-px overflow-hidden rounded-2xl border border-forest/10 bg-forest/10 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
              <FadeUp key={s.title} delay={i * 0.06} className="h-full">
                <div
                  data-testid={`service-card-${i}`}
                  className="group flex h-full flex-col bg-cream p-9 transition-colors duration-500 hover:bg-forest"
                >
                  <div className="flex items-center justify-between">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full border border-forest/15 text-forest-700 transition-colors duration-500 group-hover:border-sage/30 group-hover:text-sage">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="font-mono text-xs text-forest/30 transition-colors duration-500 group-hover:text-cream/30">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="mt-7 font-serif text-2xl font-medium text-forest transition-colors duration-500 group-hover:text-cream">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/65 transition-colors duration-500 group-hover:text-cream/65">
                    {s.desc}
                  </p>
                </div>
              </FadeUp>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

function Difference() {
  return (
    <section className="noise-dark bg-forest py-28 text-cream sm:py-36">
      <Container className="relative z-10">
        <SectionHeading
          index="03"
          eyebrow="The FieldGreen difference"
          dark
          title="Why owners choose FieldGreen."
        />
        <div className="mt-16 space-y-0">
          {DIFFERENCE.map((d, i) => (
            <FadeUp key={d.title} delay={i * 0.05}>
              <div
                data-testid={`difference-item-${i}`}
                className="group grid gap-4 border-t border-white/10 py-9 transition-colors duration-500 last:border-b hover:bg-white/[0.03] sm:grid-cols-12 sm:items-baseline sm:gap-8"
              >
                <span className="font-mono text-sm text-sage/60 sm:col-span-2">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-serif text-2xl sm:text-3xl font-medium text-cream sm:col-span-4">{d.title}</h3>
                <p className="text-sm leading-relaxed text-cream/60 sm:col-span-6">{d.desc}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </Container>
    </section>
  );
}

function Performance() {
  return (
    <section className="bg-bone py-28 sm:py-36">
      <Container>
        <SectionHeading
          index="04"
          eyebrow="Performance"
          title="Built around better property performance."
          copy="Our focus is simple: maximise the potential of each property while delivering a professional experience for both owners and guests."
        />
        <div className="mt-16 grid gap-8 lg:grid-cols-12">
          <FadeUp className="lg:col-span-5">
            <div className="tracing-card h-full rounded-2xl bg-forest p-10 text-cream" data-testid="performance-stat-card">
              <p className="eyebrow text-sage">Recent portfolio month</p>
              <p className="mt-6 font-mono text-5xl sm:text-6xl font-semibold text-cream" data-testid="performance-revenue-figure">
                <CountUp to={12000} prefix="€" suffix="+" />
              </p>
              <p className="mt-5 text-sm leading-relaxed text-cream/65">
                Revenue generated across FieldGreen's currently managed properties in a single recent month.
              </p>
              <p className="mt-6 border-t border-white/10 pt-6 text-xs leading-relaxed text-cream/40">
                Shared as an example of current portfolio performance — not a guarantee of future results.
                Every property performs differently; we'll give you a realistic assessment for yours.
              </p>
            </div>
          </FadeUp>
          <div className="grid gap-8 sm:grid-cols-2 lg:col-span-7">
            <FadeUp delay={0.1}>
              <div className="flex h-full flex-col rounded-2xl border border-forest/12 bg-cream p-9" data-testid="case-study-before-card">
                <p className="eyebrow text-charcoal/50">Before FieldGreen · Long-term let</p>
                <p className="mt-5 font-serif text-2xl font-medium text-charcoal/80">3-bed house, Swords</p>
                <p className="mt-6 font-mono text-4xl font-semibold text-charcoal/80" data-testid="case-study-before-figure">€3,000</p>
                <p className="mt-1.5 text-xs text-charcoal/50">per month — long-term rental income</p>
                <p className="mt-4 text-sm leading-relaxed text-charcoal/60">
                  A well-kept family home earning a fixed monthly rent on a standard tenancy.
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="flex h-full flex-col rounded-2xl bg-forest-700 p-9 text-cream" data-testid="case-study-after-card">
                <div className="flex items-center justify-between gap-3">
                  <p className="eyebrow text-sage-light">With FieldGreen · Short-term</p>
                  <span className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 font-mono text-xs font-semibold text-gold" data-testid="case-study-uplift-badge">
                    1.9×
                  </span>
                </div>
                <p className="mt-5 font-serif text-2xl font-medium">The same house, managed</p>
                <p className="mt-6 font-mono text-4xl font-semibold text-cream" data-testid="case-study-after-figure">
                  <CountUp to={5800} prefix="€" />
                </p>
                <p className="mt-1.5 text-xs text-cream/60">owner earnings in August</p>
                <p className="mt-4 text-sm leading-relaxed text-cream/70">
                  Repositioned as a professionally managed short-term rental — priced dynamically, presented properly.
                </p>
                <p className="mt-auto pt-6 text-[11px] leading-relaxed text-cream/40">
                  A real FieldGreen property. One month shown — short-term revenue varies with season and demand.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </Container>
    </section>
  );
}

function HowItWorksPreview() {
  return (
    <section className="bg-cream py-28 sm:py-36">
      <Container>
        <div className="flex flex-wrap items-end justify-between gap-8">
          <SectionHeading
            index="05"
            eyebrow="How it works"
            title="From first conversation to fully managed — in four steps."
          />
          <FadeUp delay={0.2}>
            <Link
              to="/how-it-works"
              data-testid="hiw-preview-link"
              className="link-underline inline-flex items-center gap-2 text-sm font-semibold text-forest-700"
            >
              See the full process <ArrowUpRight className="h-4 w-4" />
            </Link>
          </FadeUp>
        </div>
        <div className="mt-16 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <FadeUp key={s.num} delay={i * 0.08}>
              <div data-testid={`step-card-${i}`} className="group border-t-2 border-forest/15 pt-7 transition-colors duration-500 hover:border-forest-700">
                <p className="font-mono text-sm text-forest-700/60">{s.num}</p>
                <h3 className="mt-4 font-serif text-2xl font-medium leading-snug text-forest">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-charcoal/65">{s.desc}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </Container>
    </section>
  );
}

function WhoFor() {
  return (
    <section className="bg-bone py-28 sm:py-36">
      <Container>
        <SectionHeading
          index="06"
          eyebrow="Who it's for"
          title="Built for owners who'd rather not be hosts."
        />
        <div className="mt-16 grid gap-8 md:grid-cols-3">
          {WHO.map((w, i) => (
            <FadeUp key={w.title} delay={i * 0.08} className="h-full">
              <div
                data-testid={`who-card-${i}`}
                className="group flex h-full flex-col rounded-2xl border border-forest/12 bg-cream p-9 transition-all duration-500 hover:-translate-y-1.5 hover:border-forest-700/40 hover:shadow-[0_24px_60px_rgba(15,38,28,0.12)]"
              >
                <span className="font-mono text-xs text-forest-700/50">0{i + 1}</span>
                <h3 className="mt-5 font-serif text-2xl font-medium text-forest">{w.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-charcoal/65">{w.desc}</p>
                <Link
                  to="/assessment"
                  data-testid={`who-card-cta-${i}`}
                  className="mt-auto inline-flex items-center gap-2 pt-7 text-sm font-semibold text-forest-700"
                >
                  Get a Property Assessment
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </FadeUp>
          ))}
        </div>
      </Container>
    </section>
  );
}

function CorporateBand() {
  return (
    <section className="noise-dark relative overflow-hidden bg-forest-900 py-28 text-cream sm:py-32">
      <div className="absolute inset-0">
        <img src={IMAGES.dublinAerial} alt="" className="h-full w-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-forest-900/70" />
      </div>
      <Container className="relative z-10 grid items-center gap-14 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <FadeUp>
            <p className="eyebrow flex items-center gap-3 text-sage">
              <Building2 className="h-4 w-4" /> Corporate stays · 21+ days
            </p>
          </FadeUp>
          <FadeUp delay={0.08}>
            <h2 className="mt-5 font-serif text-4xl sm:text-5xl font-medium leading-[1.08]">
              Longer stays. Calmer calendars. <span className="italic text-sage-light">Stronger yields.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.16}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-cream/70">
              Alongside short-term lettings, FieldGreen places and manages corporate stays of 21 days or more —
              professionals, project teams and relocations who need a well-run Dublin base. Fewer turnovers,
              vetted guests, and the same end-to-end management. Enquiring for your company? We handle those too.
            </p>
          </FadeUp>
          <FadeUp delay={0.24}>
            <Link
              to="/corporate-stays"
              data-testid="corporate-band-cta"
              className="group mt-9 inline-flex items-center gap-2 rounded-full bg-cream px-7 py-4 text-sm font-semibold text-forest transition-colors duration-300 hover:bg-sage"
            >
              Explore Corporate Stays
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </div>
        <FadeUp delay={0.15} className="lg:col-span-5">
          <div className="overflow-hidden rounded-2xl border border-white/10">
            <img
              src={IMAGES.interiorDining}
              alt="A premium Dublin apartment prepared for a corporate stay"
              className="h-72 w-full object-cover lg:h-96"
              data-testid="corporate-band-image"
            />
          </div>
        </FadeUp>
      </Container>
    </section>
  );
}

function StayShowcase() {
  const properties = useProperties();
  return (
    <section className="bg-bone py-28 sm:py-36">
      <Container>
        <div className="flex flex-wrap items-end justify-between gap-8">
          <SectionHeading
            eyebrow="Stay with us"
            title="Travelling to Dublin? Book a FieldGreen property — direct."
            copy="Every FieldGreen stay is professionally managed, hotel-clean and bookable direct, with live availability and no platform fees."
          />
          <FadeUp delay={0.2}>
            <Link
              to="/stay"
              data-testid="stay-showcase-link"
              className="link-underline inline-flex items-center gap-2 text-sm font-semibold text-forest-700"
            >
              Browse all stays <ArrowUpRight className="h-4 w-4" />
            </Link>
          </FadeUp>
        </div>
        <div className="mt-14 grid gap-8 md:grid-cols-3">
          {properties.map((p, i) => (
            <PropertyCard key={p.id} p={p} i={i} compact />
          ))}
        </div>
        <FadeUp delay={0.25}>
          <p className="mt-12 max-w-2xl text-sm leading-relaxed text-charcoal/60" data-testid="stay-showcase-owner-note">
            Own a property that could perform like these? This is how FieldGreen presents, prices and books the
            homes we manage — across Airbnb, major channels and direct.{" "}
            <Link to="/assessment" data-testid="stay-showcase-owner-cta" className="link-underline font-semibold text-forest-700">
              See what yours could earn
            </Link>
            .
          </p>
        </FadeUp>
      </Container>
    </section>
  );
}

function Founder() {
  return (
    <section className="bg-cream py-28 sm:py-36">
      <Container className="grid items-center gap-16 lg:grid-cols-12">
        <FadeUp className="lg:col-span-5">
          <div className="relative">
            <div className="overflow-hidden rounded-2xl">
              <img
                src={IMAGES.founder}
                alt="Andrew, founder of FieldGreen Property Management"
                className="aspect-[4/5] w-full object-cover"
                data-testid="founder-photo"
              />
            </div>
            <div className="absolute -bottom-6 -right-4 rounded-xl bg-forest px-6 py-5 text-cream sm:-right-8">
              <p className="font-serif text-xl font-medium">Andrew</p>
              <p className="mt-1 text-xs uppercase tracking-[0.2em] text-sage">Founder, FieldGreen</p>
            </div>
          </div>
        </FadeUp>
        <div className="lg:col-span-7">
          <SectionHeading
            index="07"
            eyebrow="About FieldGreen"
            title="Property management built from an owner's perspective."
          />
          <FadeUp delay={0.2}>
            <div className="mt-8 space-y-5 text-base leading-relaxed text-charcoal/70">
              <p>
                FieldGreen was created around a simple idea: property owners should be able to benefit from the
                short-term rental market without taking on another full-time job.
              </p>
              <p>
                Founder Andrew spent close to a decade in Dublin's technology sales environment — and knows
                the owner's side too, as a Dublin property owner and landlord himself. FieldGreen brings those two
                worlds together: a commercially focused, data-aware approach to revenue, delivered with genuine
                hospitality standards.
              </p>
            </div>
          </FadeUp>
          <FadeUp delay={0.3}>
            <div className="mt-9 flex gap-5 rounded-2xl border border-forest/12 bg-bone p-7">
              <Quote className="h-7 w-7 shrink-0 text-forest-700/40" />
              <p className="font-serif text-xl italic leading-relaxed text-forest" data-testid="founder-quote">
                "Hospitality, technology and revenue optimisation — applied to your property, so you don't have to."
              </p>
            </div>
          </FadeUp>
          <FadeUp delay={0.38}>
            <Link
              to="/about"
              data-testid="founder-more-link"
              className="link-underline mt-8 inline-flex items-center gap-2 text-sm font-semibold text-forest-700"
            >
              More about FieldGreen <ArrowUpRight className="h-4 w-4" />
            </Link>
          </FadeUp>
        </div>
      </Container>
    </section>
  );
}

function PricingBand() {
  return (
    <section className="bg-forest-800 py-24 text-cream noise-dark">
      <Container className="relative z-10 flex flex-col items-start justify-between gap-10 lg:flex-row lg:items-center">
        <div className="max-w-xl">
          <FadeUp>
            <p className="eyebrow text-sage">Simple, transparent management</p>
            <p className="mt-5 font-serif text-4xl sm:text-5xl font-medium" data-testid="pricing-fee">
              15% <span className="text-sage-light">+ VAT</span>
            </p>
            <p className="mt-4 text-sm leading-relaxed text-cream/65">
              One management fee. No complicated packages, no hidden extras. Final pricing depends on your property
              and service requirements — which is exactly what the assessment is for.
            </p>
          </FadeUp>
        </div>
        <FadeUp delay={0.15}>
          <Link
            to="/assessment"
            data-testid="pricing-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-cream px-7 py-4 text-sm font-semibold text-forest transition-colors duration-300 hover:bg-sage"
          >
            Discuss Your Property
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </Container>
    </section>
  );
}

function FaqPreview() {
  return (
    <section className="bg-bone py-28 sm:py-36">
      <Container className="grid gap-14 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <SectionHeading
            index="08"
            eyebrow="Questions"
            title="Straight answers, before you ask."
          />
          <FadeUp delay={0.2}>
            <Link
              to="/faqs"
              data-testid="faq-preview-link"
              className="link-underline mt-8 inline-flex items-center gap-2 text-sm font-semibold text-forest-700"
            >
              View all FAQs <ArrowUpRight className="h-4 w-4" />
            </Link>
          </FadeUp>
        </div>
        <div className="lg:col-span-8">
          <FadeUp delay={0.1}>
            {FAQS.slice(0, 5).map((f, i) => (
              <AccordionItem key={f.q} q={f.q} a={f.a} testid={`home-faq-item-${i}`} />
            ))}
          </FadeUp>
        </div>
      </Container>
    </section>
  );
}

function LeadSection() {
  return (
    <section className="noise-dark bg-forest py-28 text-cream sm:py-36" data-testid="lead-section">
      <Container className="relative z-10 grid items-center gap-16 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <MaskedLines
            delay={0}
            lineClassName="font-serif text-4xl sm:text-5xl font-medium leading-[1.1]"
            lines={[
              <span key="a">Wondering what your</span>,
              <span key="b" className="italic text-sage-light">property could earn?</span>,
            ]}
          />
          <FadeUp delay={0.3}>
            <p className="mt-6 max-w-md text-base leading-relaxed text-cream/70">
              Tell us a little about your property and we'll assess whether short-term management — or a corporate
              stay strategy — could make sense for you. Honest numbers, no obligation.
            </p>
          </FadeUp>
          <FadeUp delay={0.4}>
            <ul className="mt-9 space-y-3.5">
              {[
                "Realistic revenue range based on live Dublin market data",
                "A straight answer on suitability — including when it isn't",
                "Corporate stay (21+ days) options where they fit",
              ].map((t, i) => (
                <li key={t} className="flex items-start gap-3 text-sm text-cream/75" data-testid={`lead-benefit-${i}`}>
                  <CheckCircle2 className="mt-0.5 h-4.5 w-4.5 h-5 w-5 shrink-0 text-sage" />
                  {t}
                </li>
              ))}
            </ul>
          </FadeUp>
        </div>
        <FadeUp delay={0.2} className="lg:col-span-6">
          <LeadForm source="homepage" />
        </FadeUp>
      </Container>
    </section>
  );
}

export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <div className="border-y border-white/5 bg-forest-900 py-5">
        <Marquee
          items={MARQUEE_ITEMS}
          textClassName="font-serif text-2xl sm:text-3xl italic text-sage/25 px-2"
        />
      </div>
      <Problem />
      <ServicesGrid />
      <Difference />
      <Performance />
      <HowItWorksPreview />
      <WhoFor />
      <CorporateBand />
      <StayShowcase />
      <Founder />
      <PricingBand />
      <FaqPreview />
      <LeadSection />
    </main>
  );
}
